import React, { useMemo } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import { getDepartmentRiskReport } from '../../data/reportHelpers';
import { getRiskLevel } from '../../data/helpers';
import SummaryStatBar from './SummaryStatBar';
import ExportBar from './ExportBar';

const { FiShield, FiAlertTriangle, FiTrendingUp, FiUsers } = FiIcons;

const COLUMNS = [
  { key: 'dept', label: 'Department' },
  { key: 'total', label: 'Total Learners' },
  { key: 'avgRisk', label: 'Avg Risk Score' },
  { key: 'highRisk', label: 'High Risk' },
  { key: 'medRisk', label: 'Medium Risk' },
  { key: 'lowRisk', label: 'Low Risk' },
  { key: 'clicks', label: 'Phishing Clicks' },
  { key: 'reports', label: 'Phishing Reports' },
];

const DepartmentRiskReport = ({ filters, raw }) => {
  const data = useMemo(() => getDepartmentRiskReport(raw, filters), [raw, filters]);

  const overallAvg = Math.round(data.reduce((a, b) => a + b.avgRisk, 0) / (data.length || 1));
  const stats = [
    { label: 'Departments', value: data.length, icon: FiUsers, iconColor: 'bg-blue-50 text-blue-600', border: 'border-blue-100' },
    { label: 'Avg. Risk Score', value: overallAvg, icon: FiShield, iconColor: 'bg-violet-50 text-violet-600', border: 'border-violet-100' },
    { label: 'High-Risk Depts', value: data.filter(d => d.avgRisk >= 50).length, icon: FiAlertTriangle, iconColor: 'bg-red-50 text-red-600', border: 'border-red-100' },
    { label: 'Total High-Risk', value: data.reduce((a, b) => a + b.highRisk, 0), icon: FiAlertTriangle, iconColor: 'bg-orange-50 text-orange-600', border: 'border-orange-100' },
    { label: 'Total Clicks', value: data.reduce((a, b) => a + b.clicks, 0), icon: FiTrendingUp, iconColor: 'bg-rose-50 text-rose-600', border: 'border-rose-100' },
    { label: 'Total Reports', value: data.reduce((a, b) => a + b.reports, 0), icon: FiShield, iconColor: 'bg-emerald-50 text-emerald-600', border: 'border-emerald-100' },
  ];

  const chartOption = {
    tooltip: { trigger: 'axis', formatter: '{b}: Risk Score {c}' },
    grid: { left: '3%', right: '5%', top: '5%', bottom: '5%', containLabel: true },
    xAxis: { type: 'value', max: 100, splitLine: { lineStyle: { color: '#f1f5f9' } }, axisLabel: { fontSize: 10 } },
    yAxis: { type: 'category', data: [...data].reverse().map(d => d.dept), axisLabel: { fontSize: 11 } },
    series: [{
      type: 'bar', barMaxWidth: 24,
      data: [...data].reverse().map(d => ({
        value: d.avgRisk,
        itemStyle: { color: d.avgRisk < 20 ? '#10b981' : d.avgRisk < 50 ? '#f59e0b' : '#ef4444', borderRadius: [0, 6, 6, 0] }
      }))
    }]
  };

  return (
    <div className="space-y-4">
      <SummaryStatBar stats={stats} />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Average Risk Score by Department</h3>
        <ReactECharts option={chartOption} style={{ height: 240 }} />
      </div>
      <ExportBar reportTitle="Department Risk Report" data={data} columns={COLUMNS} summaryStats={stats.map(s => ({ label: s.label, value: s.value }))} rowCount={data.length} />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <th className="p-4">Department</th>
                <th className="p-4">Learners</th>
                <th className="p-4">Avg. Risk Score</th>
                <th className="p-4">High Risk</th>
                <th className="p-4">Med Risk</th>
                <th className="p-4">Low Risk</th>
                <th className="p-4">Phishing Clicks</th>
                <th className="p-4">Reports</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {data.map(row => {
                const risk = getRiskLevel(row.avgRisk);
                const color = row.avgRisk < 20 ? '#10b981' : row.avgRisk < 50 ? '#f59e0b' : '#ef4444';
                return (
                  <tr key={row.dept} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4 font-semibold text-slate-800">{row.dept}</td>
                    <td className="p-4 text-slate-700">{row.total}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-slate-100 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full" style={{ width: `${row.avgRisk}%`, backgroundColor: color }} />
                        </div>
                        <span className="text-sm font-bold" style={{ color }}>{row.avgRisk}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${risk.color}`}>{risk.label}</span>
                      </div>
                    </td>
                    <td className="p-4 text-red-600 font-semibold">{row.highRisk}</td>
                    <td className="p-4 text-amber-600 font-semibold">{row.medRisk}</td>
                    <td className="p-4 text-emerald-600 font-semibold">{row.lowRisk}</td>
                    <td className="p-4 text-slate-700">{row.clicks}</td>
                    <td className="p-4 text-emerald-600 font-semibold">{row.reports}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DepartmentRiskReport;